/**
 * Performance Tab Component - Real customer performance metrics
 */

import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Truck, Clock } from 'lucide-react';
import { CustomerProfile } from '../../lib/api';

interface PerformanceTabProps {
  customer: CustomerProfile;
}

function formatNumber(value: number | string | undefined | null, decimals = 0): string {
  if (value === undefined || value === null) return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '-';
  return num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function StatRow({ label, value, suffix }: { label: string; value: string; suffix?: string }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-semibold">
        {value}{suffix || ''}
      </span>
    </div>
  );
}

export function PerformanceTab({ customer }: PerformanceTabProps) {
  const otdPercent = customer.on_time_delivery_rate ?? 0;

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-1.5">
              <Clock className="h-4 w-4 text-muted-foreground" />
              OTD Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatNumber(otdPercent, 1)}%
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {formatNumber(customer.ontime_count)} on-time of {formatNumber(customer.grn_count)} GRNs
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">OTD Delivery (GRN)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-border">
              <StatRow
                label="OTD Rate"
                value={`${formatNumber(otdPercent, 1)}%`}
              />
              <StatRow
                label="Total GRNs"
                value={formatNumber(customer.grn_count)}
              />
              <StatRow
                label="On-Time"
                value={formatNumber(customer.ontime_count)}
              />
              <StatRow
                label="Late"
                value={formatNumber(customer.late_count)}
              />
              <StatRow
                label="Early"
                value={formatNumber(customer.early_count)}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-1.5">
              <Truck className="h-4 w-4 text-muted-foreground" />
              Cost & Orders
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-border">
              <StatRow
                label="Total Orders"
                value={formatNumber(customer.total_orders)}
              />
              <StatRow
                label="Completed Orders"
                value={formatNumber(customer.completed_orders)}
              />
              {customer.total_value && (
                <StatRow
                  label={`Total Value${customer.currency ? ` (${customer.currency})` : ''}`}
                  value={formatNumber(customer.total_value, 2)}
                />
              )}
              {customer.carbon_emission !== undefined && customer.carbon_emission !== null && (
                <StatRow
                  label="CO₂ Emissions"
                  value={`${formatNumber(customer.carbon_emission, 2)} kg`}
                />
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
